/**
 * Iridescent Tile Pusher Visualizer
 *
 * Renders a full-screen grid of glossy "pool tile" squares that extrude upward
 * based on frequency energy.  All 3-D depth is faked with Canvas 2D shading:
 *
 *   ┌─────────────┐  ← top face  (base colour + iridescent sheen + noise texture)
 *   │  specular ●  │
 *   │             ╱│  ← right bevel  (darker, simulates side face)
 *   └────────────╱─┘
 *     shadow below     ← soft offset shadow proportional to height
 *
 * Shading model (per tile, per frame):
 *   1. Base colour: HSL with hue = f(tile position, paletteShift).
 *      The palette drifts slowly in time (hueDriftSpeed).
 *   2. Height: driven by the mapped frequency amplitude for that tile,
 *      plus a global beat impulse.  Heights use a spring model
 *      (springiness / damping) for punchy-but-smooth motion.
 *   3. Iridescence: a subtle multi-hue overlay whose hue offset depends
 *      on tile position + time + amplitude.
 *   4. Micro-texture: a pre-baked noise tile (offscreen canvas) is
 *      composited at low alpha to give a frosted-glass grain.
 *   5. Specular glints: small bright ellipses that appear on high tiles
 *      when treble energy is strong (additive blending).
 *
 * Audio → height mapping:
 *   - getElementCount() returns gridCols frequency bands.
 *   - Each column of tiles maps to one band (x → band index).
 *   - Within a column, every row receives the same base amplitude,
 *     smoothed slightly more for higher rows (y-based blending).
 *   - On beat, a global impulse lifts all tiles briefly.
 *
 * Uses offscreen canvas trail technique for soft fade that respects
 * the global background controller (solid, gradient, pattern, image).
 *
 * Performance notes:
 *   - Noise texture is pre-rendered to an offscreen canvas once
 *     (rebuilt on resize / config change).
 *   - Tile positions are precomputed (rebuilt on resize / grid change).
 *   - Per-frame: fillRect + strokeRect per tile, sparse gradients for
 *     bevels, and a handful of glint ellipses with additive blending.
 *   - Zero-alloc HSL string helpers at module scope.
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';
import { mapFrequencyData, smoothAmplitudes } from '../lib/audio-utils.js';

// ── Metadata (used by manifest generation) ─────────────────────────
export const metadata = {
  name: 'iridescent-tile-pusher',
  displayName: 'Iridescent Tile Pusher',
  contextType: '2d',
};

// ── Helpers (module-scope, zero-alloc per frame) ────────────────────

/** Clamp value between lo and hi. */
function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

/** Fast HSL → CSS string (avoids template-literal in hot path). */
function hsl(h, s, l) { return 'hsl(' + (h | 0) + ',' + (s | 0) + '%,' + (l | 0) + '%)'; }
function hsla(h, s, l, a) { return 'hsla(' + (h | 0) + ',' + (s | 0) + '%,' + (l | 0) + '%,' + a.toFixed(3) + ')'; }

// ════════════════════════════════════════════════════════════════════
class IridescentTilePusher extends Base2DVisualizer {
  static name = 'iridescent-tile-pusher';
  static displayName = 'Iridescent Tile Pusher';

  // ── Config UI (section-organized for UX) ──────────────────────────
  static getConfigUI() {
    return [
      // Grid
      { type: 'section', label: 'Grid' },
      { type: 'range', id: 'gridCols',      label: 'Columns',         min: 6,  max: 50,  default: 14 },
      { type: 'range', id: 'gridRows',      label: 'Rows',            min: 4,  max: 35,  default: 10 },
      { type: 'range', id: 'tileGap',       label: 'Tile Gap',        min: 0,  max: 12,  default: 3 },
      { type: 'range', id: 'tileRounding',  label: 'Corner Rounding', min: 0,  max: 20,  default: 4 },

      // Push / spring
      { type: 'section', label: 'Push Effect' },
      { type: 'range', id: 'heightScale',    label: 'Push Height',    min: 5,  max: 80,  default: 40 },
      { type: 'range', id: 'springiness',    label: 'Springiness',    min: 5,  max: 60,  default: 30, scale: 0.01 },
      { type: 'range', id: 'damping',        label: 'Damping',        min: 10, max: 95,  default: 75, scale: 0.01 },
      { type: 'range', id: 'perspectiveX',   label: 'Perspective X',  min: 0,  max: 50,  default: 25, scale: 0.01 },
      { type: 'range', id: 'perspectiveY',   label: 'Perspective Y',  min: 10, max: 80,  default: 50, scale: 0.01 },

      // Iridescence / palette
      { type: 'section', label: 'Iridescence' },
      { type: 'range', id: 'iridescenceStrength', label: 'Iridescence',   min: 0,  max: 100, default: 50, scale: 0.01 },
      { type: 'range', id: 'hueDriftSpeed',       label: 'Hue Drift',     min: 0,  max: 40,  default: 12, scale: 0.01 },
      { type: 'range', id: 'hueSpread',           label: 'Hue Spread',    min: 30, max: 360, default: 220 },
      { type: 'range', id: 'saturation',           label: 'Saturation',   min: 20, max: 100, default: 75 },
      { type: 'range', id: 'shimmerAmount',        label: 'Shimmer',      min: 0,  max: 100, default: 40, scale: 0.01 },

      // Effects
      { type: 'section', label: 'Effects' },
      { type: 'range', id: 'bevelStrength',  label: 'Bevel Strength',  min: 0,  max: 100, default: 60, scale: 0.01 },
      { type: 'range', id: 'glintDensity',   label: 'Glint Density',   min: 0,  max: 100, default: 50, scale: 0.01 },
      { type: 'range', id: 'glintSize',      label: 'Glint Size',      min: 1,  max: 10,  default: 4 },
      { type: 'range', id: 'glowIntensity',  label: 'Glow Intensity',  min: 0,  max: 25,  default: 8, scale: 0.01 },
      { type: 'range', id: 'trailAlpha',     label: 'Trail Fade',      min: 3,  max: 40,  default: 12, scale: 0.01 },
      { type: 'range', id: 'brightness',     label: 'Brightness',      min: 20, max: 100, default: 65, scale: 0.01 },

      // Safety
      { type: 'section', label: 'Safety' },
      { type: 'range',    id: 'smoothing',   label: 'Audio Smoothing', min: 0, max: 95, default: 75, scale: 0.01 },
      { type: 'checkbox', id: 'safeMode',    label: 'Reduced Motion',  default: false },
    ];
  }

  getDefaultConfig() {
    return { glow: false }; // we handle our own highlights
  }

  getElementCount() {
    return this.config.gridCols || 14;
  }

  // ── Initialisation ────────────────────────────────────────────────
  init() {
    // Trail-fade offscreen canvas
    this._trailCanvas = null;
    this._trailCtx = null;
    this.isFirstFrame = true;

    // Noise texture (small, tiled)
    this._noiseCanvas = null;
    this._noisePattern = null;

    // Time accumulator (seconds)
    this._time = 0;

    // Beat impulse (global, decays each frame)
    this._beatImpulse = 0;

    // Previous bass value for fallback beat detection
    this._prevBassAvg = 0;

    // Per-tile spring state: current height, velocity
    // Flattened: index = row * cols + col
    this._heights = null;
    this._velocities = null;
    this._targetHeights = null;

    // Pre-computed tile rects (rebuilt on resize / config change)
    this._tileX = null;
    this._tileY = null;
    this._tileW = 0;
    this._tileH = 0;
    this._lastCanvasW = 0;
    this._lastCanvasH = 0;

    // Glint pool (reused each frame)
    this._glints = [];

    this._rebuildNoiseTexture();
    this._rebuildTileLayout();
    this._initSpringArrays();
  }

  onConfigChange(changed) {
    if (changed.gridCols !== undefined || changed.gridRows !== undefined) {
      this._initSpringArrays();
      this._rebuildTileLayout();
      this.previousAmplitudes = [];
    }
  }

  // ── Spring arrays ─────────────────────────────────────────────────
  _initSpringArrays() {
    const cols = this.config.gridCols || 14;
    const rows = this.config.gridRows || 10;
    const n = cols * rows;
    this._heights = new Float32Array(n);
    this._velocities = new Float32Array(n);
    this._targetHeights = new Float32Array(n);
  }

  // ── Tile layout (precomputed rects) ───────────────────────────────
  _rebuildTileLayout() {
    const { canvas, config } = this;
    const cols = config.gridCols || 14;
    const rows = config.gridRows || 10;
    const w = canvas.width;
    const h = canvas.height;
    this._lastCanvasW = w;
    this._lastCanvasH = h;

    this._tileW = w / cols;
    this._tileH = h / rows;

    this._tileX = new Float32Array(cols);
    this._tileY = new Float32Array(rows);
    for (let c = 0; c < cols; c++) this._tileX[c] = c * this._tileW;
    for (let r = 0; r < rows; r++) this._tileY[r] = r * this._tileH;
  }

  // ── Noise texture (pre-baked, tiled) ──────────────────────────────
  _rebuildNoiseTexture() {
    const size = 64;
    let nc;
    if (typeof OffscreenCanvas !== 'undefined') {
      nc = new OffscreenCanvas(size, size);
    } else {
      nc = document.createElement('canvas');
      nc.width = size;
      nc.height = size;
    }
    const nctx = nc.getContext('2d');
    const id = nctx.createImageData(size, size);
    const d = id.data;

    for (let i = 0; i < d.length; i += 4) {
      const v = (Math.random() * 60 + 98) | 0;
      d[i]     = v;
      d[i + 1] = v;
      d[i + 2] = v;
      d[i + 3] = 30;
    }
    nctx.putImageData(id, 0, 0);
    this._noiseCanvas = nc;
    this._noisePattern = null;
  }

  // ── Ensure offscreen canvases match main canvas size ──────────────
  _ensureTrailCanvas() {
    const { canvas } = this;
    if (!this._trailCanvas || this._trailCanvas.width !== canvas.width || this._trailCanvas.height !== canvas.height) {
      if (typeof OffscreenCanvas !== 'undefined') {
        this._trailCanvas = new OffscreenCanvas(canvas.width, canvas.height);
      } else {
        this._trailCanvas = document.createElement('canvas');
        this._trailCanvas.width = canvas.width;
        this._trailCanvas.height = canvas.height;
      }
      this._trailCtx = this._trailCanvas.getContext('2d');
    }
  }

  // ── Rounded rect helper ───────────────────────────────────────────
  _roundRect(ctx, x, y, w, h, r) {
    if (r <= 0 || !ctx.roundRect) {
      ctx.rect(x, y, w, h);
      return;
    }
    r = Math.min(r, w / 2, h / 2);
    ctx.roundRect(x, y, w, h, r);
  }

  // ── Override draw() for trail-fade technique ──────────────────────
  draw(frequencyData, features = null) {
    const elementCount = this.getElementCount();

    if (!this._freqBuffer || this._freqBuffer.length !== elementCount) {
      this._freqBuffer = new Array(elementCount).fill(0);
    }
    if (this.previousAmplitudes.length !== elementCount) {
      this.previousAmplitudes = new Array(elementCount).fill(0);
    }

    const raw = mapFrequencyData(frequencyData, elementCount, this._freqBuffer);
    const amplitudes = smoothAmplitudes(raw, this.previousAmplitudes, this.config.smoothing);

    this._currentFeatures = features;

    const { ctx, canvas } = this;

    // Rebuild layout if canvas resized
    if (canvas.width !== this._lastCanvasW || canvas.height !== this._lastCanvasH) {
      this._rebuildTileLayout();
      this._noisePattern = null;
    }

    // Trail fade (offscreen canvas technique)
    if (this.isFirstFrame) {
      this.clearCanvas();
      this.isFirstFrame = false;
    } else {
      this._ensureTrailCanvas();
      this._trailCtx.clearRect(0, 0, canvas.width, canvas.height);
      this._trailCtx.drawImage(canvas, 0, 0);
      this.clearCanvas();
      ctx.save();
      ctx.globalAlpha = 1 - this.config.trailAlpha;
      ctx.drawImage(this._trailCanvas, 0, 0);
      ctx.restore();
    }

    this.renderFrame(amplitudes, features);
  }

  // ── Main render ───────────────────────────────────────────────────
  renderFrame(amplitudes, features) {
    const { ctx, canvas, config } = this;
    const dt = 1 / 60;
    this._time += dt;

    const cols = config.gridCols || 14;
    const rows = config.gridRows || 10;
    const safe = config.safeMode;
    const tw = this._tileW;
    const th = this._tileH;
    const gap = config.tileGap || 3;
    const halfGap = gap * 0.5;
    const rounding = config.tileRounding || 4;
    const brightness = config.brightness || 0.65;

    // ─── Audio analysis ─────────────────────────────────────────
    const third = Math.floor(amplitudes.length / 3);
    let bassSum = 0, midSum = 0, trebleSum = 0;
    for (let i = 0; i < third; i++) bassSum += amplitudes[i];
    for (let i = third; i < third * 2; i++) midSum += amplitudes[i];
    for (let i = third * 2; i < amplitudes.length; i++) trebleSum += amplitudes[i];
    const bassEnergy = third > 0 ? bassSum / third : 0;
    const midEnergy = third > 0 ? midSum / third : 0;
    const trebleEnergy = (amplitudes.length - third * 2) > 0 ? trebleSum / (amplitudes.length - third * 2) : 0;

    // Beat detection: use features if available, else infer from bass spike
    let beatHit = false;
    if (features && features.rhythm) {
      beatHit = features.rhythm.isOnset;
    } else {
      const bassDelta = bassEnergy - this._prevBassAvg;
      if (bassDelta > 0.15) beatHit = true;
    }
    this._prevBassAvg = this._prevBassAvg * 0.9 + bassEnergy * 0.1;

    // Beat impulse (decays quickly)
    if (beatHit && !safe) {
      this._beatImpulse = clamp(this._beatImpulse + 0.6, 0, 1);
    }
    this._beatImpulse *= 0.88;
    if (this._beatImpulse < 0.001) this._beatImpulse = 0;

    // ─── Compute target heights per tile (spring physics) ───────
    const heightScale = config.heightScale || 40;
    const springK = config.springiness || 0.3;
    const damp = config.damping || 0.75;

    for (let c = 0; c < cols; c++) {
      const bandAmp = amplitudes[c] || 0;
      for (let r = 0; r < rows; r++) {
        const idx = r * cols + c;
        // Y-blending: higher rows get bass blended in
        const rowBlend = r / Math.max(rows - 1, 1);
        const smoothedAmp = bandAmp * (1 - rowBlend * 0.35) + bassEnergy * rowBlend * 0.25;
        const target = (smoothedAmp + this._beatImpulse * 0.5) * heightScale;
        this._targetHeights[idx] = target;

        // Spring physics: acceleration toward target, with damping
        const diff = target - this._heights[idx];
        this._velocities[idx] += diff * springK;
        this._velocities[idx] *= damp;
        this._heights[idx] += this._velocities[idx];

        if (this._heights[idx] < 0) {
          this._heights[idx] = 0;
          this._velocities[idx] = 0;
        }
      }
    }

    // ─── Ensure noise pattern ───────────────────────────────────
    if (!this._noisePattern && this._noiseCanvas) {
      try {
        this._noisePattern = ctx.createPattern(this._noiseCanvas, 'repeat');
      } catch (e) { /* OffscreenCanvas pattern may not work in all browsers */ }
    }

    // ─── Palette / iridescence parameters ───────────────────────
    const baseHue = (config.hueSpread || 220) + this._time * (config.hueDriftSpeed || 0.12) * 10;
    const iriStrength = config.iridescenceStrength || 0.5;
    const bevelStr = config.bevelStrength || 0.6;
    const sat = config.saturation || 75;
    const shimmer = config.shimmerAmount || 0.4;
    const perspX = config.perspectiveX || 0.25;
    const perspY = config.perspectiveY || 0.5;

    // ─── Draw tiles (shadow → bevel → top face) ────────────────
    // Back-to-front: top rows first so shadows don't overdraw
    this._glints.length = 0;
    const maxGlints = safe ? 3 : Math.floor((config.glintDensity || 0.5) * cols * 0.8);
    const glintThreshold = 0.35;

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const idx = r * cols + c;
        const h = this._heights[idx];
        const x = this._tileX[c] + halfGap;
        const y = this._tileY[r] + halfGap;
        const w = tw - gap;
        const ht = th - gap;

        if (w <= 0 || ht <= 0) continue;

        const hNorm = clamp(h / heightScale, 0, 1);
        const bevelPx = Math.round(1 + hNorm * bevelStr * 4);
        const effectiveRound = Math.min(rounding, w / 2, ht / 2);

        // ── Tile shadow ─────────────────────────────────────────
        const shadowOff = hNorm * perspY * 6;
        if (shadowOff > 0.5) {
          ctx.fillStyle = 'rgba(0,0,0,' + clamp(hNorm * 0.35, 0, 0.35).toFixed(3) + ')';
          ctx.beginPath();
          this._roundRect(ctx, x + shadowOff * 0.5, y + shadowOff, w, ht, effectiveRound);
          ctx.fill();
        }

        // ── Bevel / side faces ──────────────────────────────────
        if (bevelPx > 0 && bevelStr > 0.01) {
          const bevelHue = baseHue + c * 2.5 + r * 1.5;
          // Right side face
          ctx.fillStyle = hsl(bevelHue, sat * 0.5, clamp(15 + hNorm * 10, 15, 30) * brightness);
          ctx.fillRect(x + w - bevelPx, y + bevelPx, bevelPx, ht - bevelPx);
          // Bottom side face
          ctx.fillRect(x, y + ht - bevelPx, w, bevelPx);
        }

        // ── Top face ────────────────────────────────────────────
        const tileHue = (baseHue + c * (config.hueSpread / cols) + r * 2.0
          + trebleEnergy * shimmer * 40) % 360;
        const tileSat = clamp(sat + hNorm * 10 + midEnergy * 8, 30, 95);
        const tileLit = clamp((20 + hNorm * 25 + bassEnergy * 8) * brightness, 12, 65);

        ctx.fillStyle = hsl(tileHue, tileSat, tileLit);
        ctx.beginPath();
        this._roundRect(ctx, x, y, w - bevelPx, ht - bevelPx, effectiveRound);
        ctx.fill();

        // Noise texture overlay
        if (this._noisePattern) {
          ctx.save();
          ctx.globalAlpha = clamp(0.08 + hNorm * 0.06, 0.05, 0.18);
          ctx.fillStyle = this._noisePattern;
          ctx.beginPath();
          this._roundRect(ctx, x, y, w - bevelPx, ht - bevelPx, effectiveRound);
          ctx.fill();
          ctx.restore();
        }

        // Iridescent sheen — multi-hue gradient overlay
        if (iriStrength > 0.01 && !safe) {
          const iriHue = (tileHue + 60 + this._time * 15 + hNorm * 40 + bassEnergy * 30) % 360;
          const iriAlpha = clamp(iriStrength * (0.08 + hNorm * 0.12 + bassEnergy * 0.06), 0, 0.25);
          const iriGrad = ctx.createLinearGradient(x, y, x + w, y + ht);
          iriGrad.addColorStop(0, hsla(iriHue, 70, 60, iriAlpha));
          iriGrad.addColorStop(0.5, hsla(iriHue + 40, 65, 55, iriAlpha * 0.5));
          iriGrad.addColorStop(1, hsla(iriHue + 80, 70, 50, iriAlpha * 0.7));
          ctx.fillStyle = iriGrad;
          ctx.beginPath();
          this._roundRect(ctx, x, y, w - bevelPx, ht - bevelPx, effectiveRound);
          ctx.fill();
        }

        // Rim light on top and left edges
        if (hNorm > 0.05) {
          const rimAlpha = clamp(hNorm * 0.25, 0, 0.2);
          ctx.strokeStyle = hsla(tileHue + 20, 50, 80, rimAlpha);
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(x, y + ht - bevelPx);
          ctx.lineTo(x, y);
          ctx.lineTo(x + w - bevelPx, y);
          ctx.stroke();
        }

        // ── Glint candidate ─────────────────────────────────────
        if (this._glints.length < maxGlints && hNorm > glintThreshold && trebleEnergy > 0.15) {
          const prob = hNorm * trebleEnergy * (config.glintDensity || 0.5) * 2;
          const seed = Math.sin(c * 127.1 + r * 311.7 + this._time * 3.7) * 43758.5453;
          const rnd = seed - Math.floor(seed);
          if (rnd < prob) {
            this._glints.push({
              x: x + (w - bevelPx) * (0.2 + rnd * 0.6),
              y: y + (ht - bevelPx) * (0.15 + (1 - rnd) * 0.5),
              rx: clamp(config.glintSize * (0.5 + rnd), 1, 8),
              ry: clamp(config.glintSize * (0.3 + rnd * 0.4), 0.5, 5),
              hue: (tileHue + 30) % 360,
              intensity: hNorm,
            });
          }
        }
      }
    }

    // ─── Specular glints (additive blending) ────────────────────
    if (this._glints.length > 0) {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';

      for (let g = 0; g < this._glints.length; g++) {
        const gl = this._glints[g];
        const alpha = clamp(gl.intensity * (0.4 + trebleEnergy * 0.6), 0, 0.85);
        const glintLit = clamp(70 + gl.intensity * 25, 70, 95);
        const glintSat = clamp(40 - gl.intensity * 30, 5, 45);

        ctx.fillStyle = hsla(gl.hue, glintSat, glintLit, alpha);
        ctx.beginPath();
        ctx.ellipse(gl.x, gl.y, gl.rx, gl.ry, 0, 0, Math.PI * 2);
        ctx.fill();

        // Tiny bright core on very active tiles
        if (gl.intensity > 0.7) {
          ctx.fillStyle = 'rgba(255,255,255,' + (alpha * 0.5).toFixed(3) + ')';
          ctx.beginPath();
          ctx.ellipse(gl.x, gl.y, gl.rx * 0.3, gl.ry * 0.3, 0, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.restore();
    }

    // ─── Bass-energy border glow ────────────────────────────────
    const glowInt = config.glowIntensity || 0.08;
    if (glowInt > 0.01 && bassEnergy > 0.15 && !safe) {
      const glowAlpha = clamp(bassEnergy * glowInt * 3, 0, 0.4);
      const glowHue = (baseHue + 90) % 360;
      ctx.save();
      ctx.shadowBlur = 15 + bassEnergy * 20;
      ctx.shadowColor = hsla(glowHue, 70, 55, glowAlpha);
      ctx.strokeStyle = hsla(glowHue, 60, 50, glowAlpha * 0.6);
      ctx.lineWidth = 2;
      ctx.strokeRect(4, 4, canvas.width - 8, canvas.height - 8);
      ctx.restore();
    }
  }

  // ── Clear / reset ─────────────────────────────────────────────────
  clear() {
    super.clear();
    this.isFirstFrame = true;
    this._time = 0;
    this._beatImpulse = 0;
    this._prevBassAvg = 0;

    if (this._heights) this._heights.fill(0);
    if (this._velocities) this._velocities.fill(0);
    if (this._targetHeights) this._targetHeights.fill(0);

    if (this._trailCtx && this._trailCanvas) {
      this._trailCtx.clearRect(0, 0, this._trailCanvas.width, this._trailCanvas.height);
    }
  }
}

// ── Register with the visualizer registry ───────────────────────────
VisualizerRegistry.register(IridescentTilePusher);

export { IridescentTilePusher };
export default IridescentTilePusher;
